import React, { useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import { Pagination } from '../../../components/Pagination';
import moment from 'moment';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { crmService } from '../../../services/central-api/crm';
import MyDataTable from '../../../components/MyDataTable';
import { CRMParamList } from '../../../typings/Params';
import Tooltip from '../../../components/Tooltip';
import CustomButton from '../../../components/CustomButton';
import { useAuth } from '../../../context/Auth';
import useFilters from '../../../components/Filter/hooks/useFilters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Filter from './Filters';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import DownloadReportModal from './DownloadReportModal';
import { useCurrentApp } from '../../../context/CurrentApp';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import getBestContrastColor from '../../../services/best-contrast-color';
import Badge from '../../../components/Badge';

export default function Management({ navigation }: { navigation: Navigation; }) {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();
    const route = useRoute<RouteProp<CRMParamList, 'Management'>>();
    const [filters, setFilters] = useFilters<CRMParamList['Management']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [isDownloadModalVisible, setIsDownloadModalVisible] = useState(false);
    const { isParentApp } = useCurrentApp();

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getCamerasPaginated', filters],
        queryFn: () => crmService.getCamerasPaginated({
            ...filters,
            showDisabled: true,
            hideChildren: false,
        }),
        enabled: !!authenticatedUser && isFocused,
    });
    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data } = queryResult;

    return (
        <Container>
            <Header
                title={translate('cameras')}
                actions={<>
                    {isParentApp && (authenticatedUser?.isAdmin || authenticatedUser?.permissions.camera_download_report)
                        ? <CustomButton
                            onPress={() => setIsDownloadModalVisible(true)}
                            text={translate('downloadReport')}
                            type='secondary'
                        />
                        : null
                    }
                    <CustomButton onPress={() => navigation.navigate('ManagementDetails')} text={translate('register')} />
                </>}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredCameras')}
                    zIndex={2}
                    filter={<Filter
                        filters={filters}
                        setFilters={setFilters}
                        textFilter={textFilter}
                        setTextFilter={setTextFilter}
                    />}
                    filterInput={<FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('company'),
                                selector: row => row.company?.name || '',
                                grow: 2,
                                wrap: true,
                                omit: !isParentApp
                            },
                            {
                                name: translate('title'),
                                selector: row => row.title,
                                grow: 3,
                                wrap: true,
                            },
                            {
                                name: translate('manufacturer'),
                                selector: row => row.manufacturer ? translate(row.manufacturer) : '',
                            },
                            {
                                name: translate('installationCompany'),
                                selector: row => row.installationCompany?.name || '',
                            },
                            {
                                name: translate('internetType'),
                                selector: row => row.internetType == '5g' ? '5G' : row.internetType == '4g' ? '4G' : row.internetType == 'fiber' ? translate('fiber') : translate('radio'),
                            },
                            {
                                name: translate('serialNo'),
                                selector: row => row.serialNo || '',
                            },
                            {
                                name: translate('type'),
                                selector: row => translate(row.type),
                            },
                            {
                                name: translate('status'),
                                cell: row => {
                                    if (row.stolen) {
                                        return <MyAppText style={[styles.cell, { color: theme.colors.disabledText }]}>
                                            {translate('stolen_equipment')}
                                        </MyAppText>;
                                    }

                                    return row.lastTimeOnline ?
                                        <Tooltip tooltipDirection='right' tooltipContent={translate('offlineSince', {
                                            lastTimeOnline: moment(row.lastTimeOnline).format('DD/MM/YYYY HH:mm')
                                        })} style={{ width: 300 }}>
                                            <MyAppText style={[styles.cell, row.disabledAt ? { color: theme.colors.disabledText } : row.isConnected ? {} : { color: '#FF0027' }]}>
                                                {row.disabledAt ? translate('disabled') : row.isConnected ? translate('online') : translate('offline')}
                                            </MyAppText>
                                        </Tooltip>
                                        : <MyAppText style={[styles.cell, row.disabledAt ? { color: theme.colors.disabledText } : row.isConnected ? {} : { color: '#FF0027' }]}>
                                            {row.disabledAt ? translate('disabled') : row.isConnected ? translate('online') : translate('offline')}
                                        </MyAppText>;
                                }
                            },
                            {
                                name: translate('tags'),
                                grow: 3,
                                cell: row => {
                                    if (!row.tags) {
                                        return '';
                                    }

                                    const tags = row.tags.map((tag) =>
                                        <Badge
                                            mode='ROUNDED'
                                            key={tag.id}
                                            backgroundColor={tag.color}
                                            textColor={getBestContrastColor(tag.color)}>
                                            {tag.name}
                                        </Badge>
                                    );

                                    return <View style={{
                                        flexDirection: 'row',
                                        flexWrap: 'wrap',
                                        width: '100%',
                                        gap: 5
                                    }}>
                                        {tags}
                                    </View>;
                                },
                            },
                            {
                                name: translate('sendingEvents'),
                                cell: row => {
                                    if (row.stolen && row.disabledAt && (!row.hasFacial || !row.hasLpr)) {
                                        return <MyAppText style={[styles.cell, { color: theme.colors.disabledText }]}>
                                            {'-'}
                                        </MyAppText>;
                                    }

                                    if (row.lastFacialEvent || row.lastLprEvent) {
                                        return <Tooltip
                                            tooltipDirection='right'
                                            tooltipContent={translate('offlineSince', {
                                                lastTimeOnline: moment(row.lastFacialEvent ?? row.lastLprEvent).format('DD/MM/YYYY HH:mm')
                                            })}>
                                            <MyAppText style={[styles.cell, { color: '#FF0027' }]}>{translate('inactive')}</MyAppText>
                                        </Tooltip>;
                                    } else {
                                        return <Tooltip tooltipDirection='right' tooltipContent={translate('cameraIsActiveTooltip')}>
                                            <MyAppText style={styles.cell}>{translate('active')}</MyAppText>
                                        </Tooltip>;
                                    }
                                },
                                omit: !isParentApp
                            },
                            {
                                name: translate('ptzStatus'),
                                cell: row => {
                                    if (row.stolen || row.type !== 'ptz' || row.disabledAt) {
                                        return <MyAppText style={[styles.cell, { color: theme.colors.disabledText }]}>
                                            {'-'}
                                        </MyAppText>;
                                    }

                                    return <MyAppText style={[styles.cell, row.ptzStatus ? {} : { color: '#FF0027' }]}>
                                        {row.ptzStatus ? translate('online') : translate('offline')}
                                    </MyAppText>;
                                },
                                omit: !isParentApp
                            },
                            {
                                name: translate('registeredAt'),
                                selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm'),
                                wrap: true
                            },
                            {
                                button: true,
                                cell: row => {
                                    const actions: Action[] = [];
                                    actions.push({
                                        content: translate('watchCamera'),
                                        onPress: () => {
                                            navigation.navigate('CameraPlayer', { id: row.id });
                                        },
                                    });
                                    actions.push({
                                        content: translate('watchCameraOnNewTab'),
                                        onPress: () => {
                                            window.open(`/cameras/map/${row.id}`);
                                        },
                                    });
                                    actions.push({
                                        content: translate('editInformation'),
                                        onPress: () => navigation.navigate('ManagementDetails', { id: row.id })
                                    });
                                    actions.push({
                                        content: translate('cameraDisconnectionHistory'),
                                        onPress: () => navigation.navigate('CameraHistory', {
                                            id: row.id,
                                        })
                                    });
                                    return <DropdownButton actions={actions} />;
                                }
                            }
                        ]}
                        data={data?.rows ?? []}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={data?.count ?? 0}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>

            <DownloadReportModal
                isModalVisible={isDownloadModalVisible}
                setModalVisible={setIsDownloadModalVisible}
            />
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    cell: {
        fontSize: 14,
        color: theme.colors.textColor,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
    },
}));

